import {memo, type ReactNode} from 'react'
import {ScopedCommands} from '@github-ui/ui-commands'
import {capitalize} from '@github-ui/filter-core'
import {RadioGroup, FormControl, Radio, BranchName} from '@primer/react'
import {ReviewEvent} from '../../mutations/use-submit-review-mutation'
import type {AllowedNonCommentReviewType} from '../../page-data/payloads/toolbar'
import styles from './ReviewTypeSelector.module.css'
import {PullRequestState} from '../../types'
import {isReviewablePullRequestState} from './is-reviewable-pull-request-state'
import {usePullRequestState} from '../../hooks/use-pull-request-state'

export function ReviewTypeSelector({
  reviewEvent,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  handleReviewEventChange,
  handleReviewSubmit,
  stackBaseBranch,
}: {
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy?: boolean | null
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
  handleReviewEventChange: (newReviewEvent: string | null) => void
  handleReviewSubmit: () => void
  stackBaseBranch?: string
}) {
  const pullRequestState = usePullRequestState()
  const isInReviewableState = isReviewablePullRequestState(pullRequestState)

  if (!isInReviewableState) {
    return null
  }

  return (
    <ScopedCommands
      commands={{
        'pull-request-files-changed:submit-review': handleReviewSubmit,
      }}
    >
      <ReviewRadioButtons
        pullRequestState={pullRequestState}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        onReviewEventChange={handleReviewEventChange}
        stackBaseBranch={stackBaseBranch}
      />
    </ScopedCommands>
  )
}

export const ReviewRadioButtons = memo(function ReviewRadioButtonsComponent({
  pullRequestState,
  onReviewEventChange,
  reviewEvent,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  stackBaseBranch,
}: {
  pullRequestState: PullRequestState
  onReviewEventChange: (selected: string | null) => void
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy: boolean | null | undefined
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
  stackBaseBranch?: string
}) {
  const approveDisabled = !viewerAllowedNonCommentReviewTypes.includes('APPROVE')
  const requestChangesDisabled = !viewerAllowedNonCommentReviewTypes.includes('REQUEST_CHANGES')

  return (
    <RadioGroup name="reviewEvent" onChange={onReviewEventChange} className={styles.RadioGroup}>
      <RadioGroup.Label visuallyHidden>Review event</RadioGroup.Label>
      <RadioButton
        checked={reviewEvent === ReviewEvent.comment}
        label={capitalize(ReviewEvent.comment)}
        subLabel="Submit general feedback without explicit approval."
        value={ReviewEvent.comment}
      />
      <RadioButton
        checked={reviewEvent === ReviewEvent.approve}
        disabled={approveDisabled}
        label={capitalize(ReviewEvent.approve)}
        subLabel={
          approveDisabled
            ? getApproveDisabledReason(viewerIsAuthor, !!viewerHasViolatedPushPolicy)
            : getApproveSubLabel(viewerIsCopilotAttributed, pullRequestState, stackBaseBranch)
        }
        value={ReviewEvent.approve}
      />
      <RadioButton
        checked={reviewEvent === ReviewEvent.requestChanges}
        disabled={requestChangesDisabled}
        label={capitalize(ReviewEvent.requestChanges)}
        value={ReviewEvent.requestChanges}
        subLabel={
          requestChangesDisabled
            ? getRequestChangesDisabledReason(viewerIsAuthor)
            : getRequestChangesSubLabel(viewerCanWriteToRepo, pullRequestState)
        }
      />
    </RadioGroup>
  )
})

function RadioButton({
  checked,
  disabled,
  label,
  subLabel,
  value,
}: {
  checked?: boolean
  disabled?: boolean
  label: string
  subLabel: ReactNode
  value: string
}) {
  return (
    <FormControl disabled={disabled}>
      <Radio checked={checked} value={value} className={styles.Radio} />
      <FormControl.Label className="d-flex flex-column">
        <span className="text-bold">{label}</span>
        <span className={styles.RadioText}>{subLabel}</span>
      </FormControl.Label>
    </FormControl>
  )
}

function getApproveDisabledReason(reviewerIsAuthor: boolean, violatedPushPolicy: boolean) {
  if (reviewerIsAuthor) return "Pull request authors can't approve their own pull requests."
  if (violatedPushPolicy) return "Users that pushed changes to this pull request after it was opened can't approve"
  return 'Only users with explicit access to this repository may approve pull requests'
}

function getRequestChangesDisabledReason(reviewerIsAuthor: boolean) {
  if (reviewerIsAuthor) return "Pull request authors can't request changes on their own pull requests."
  return 'Only users with explicit access to this repository may request changes on pull requests'
}

function getApproveSubLabel(
  viewerIsCopilotAttributed: boolean,
  pullRequestState: PullRequestState,
  stackBaseBranch?: string,
) {
  if (pullRequestState === PullRequestState.Merged) {
    return 'Submit feedback and record your approval of these merged changes.'
  }

  if (viewerIsCopilotAttributed) {
    return 'Only users who did not collaborate with the cloud agent will satisfy review requirements.'
  }

  if (stackBaseBranch) {
    return (
      <>
        Submit feedback and approve merging these changes into <BranchName as="span">{stackBaseBranch}</BranchName>.
      </>
    )
  }

  return 'Submit feedback and approve merging these changes.'
}

function getRequestChangesSubLabel(viewerCanWriteToRepo: boolean, pullRequestState: PullRequestState) {
  if (pullRequestState === PullRequestState.Merged) {
    return 'Submit feedback suggesting follow-up changes.'
  }

  if (!viewerCanWriteToRepo) {
    return 'Submit feedback suggesting changes.'
  }

  return 'Submit feedback that must be addressed before merging.'
}
